import type Highcharts from "highcharts";
import React from "react";
import * as io from "io-ts";
import { renderToString } from "react-dom/server";
import validateInterface from "@ui/lib/validateInterface";
import type { Action } from "@ui/Highcharts/ui/Tooltip";
import { /* FlagDescription, */ FLAG_AXIS } from "../../Chart/series/getAnalysisFlagSerie";
import {
  PointMetadata,
  MEASURE_POINT_METADATA,
  QUANTITY_FLAG_METADATA,
  GAP_POINT_METADATA,
} from "../../Chart/PointMetadata";
import useAnalysisXAxis from "../../Chart/hooks/useAnalysisXAxis";
import useQuantityYAxes, { getAxisIds } from "../../Chart/hooks/useQuantityYAxes";
import useYMinMax from "@ui/Highcharts/hooks/useYMinMax";
import type { SelectedQuantity } from "../../useSelectedQuantities";
import type { Options } from "@ui/Highcharts";
import { getMainAxisSelectedPoints } from "@ui/Highcharts/plugins/stickyTooltip";
import { useQuantityNoDataIntervals } from "../../useNoDataIntervals";
import t from "../../../../i18n";
import useNoDataPlotBands from "@ui/Highcharts/hooks/useNoDataPlotBands";
import type { ActiveAnalysis } from "../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../api/useProjects";
import type { Source } from "../../../../api/useSources";
import useToasts from "@ui/Toast/useToasts";
import useUpdateMetrologyOverrides from "../../Chart/useUpdateMetrologyOverrides";
import useTooltipClick from "@ui/Highcharts/hooks/useTooltipClick";
import useGroupBreaks from "../../Chart/hooks/useGroupBreaks";
import { capitalize } from "diagnostics-lib/string";
import Text from "@ui/Text";
import { QuantityChartTooltip } from "../../Chart/ChartTooltip/data_symptom/QuantityChartTooltip";
import { useQuantityByName } from "../../../../api/useQuantities";
import formatNumber from "@ui/i18n/formatNumber";
import {
  QuantityValidityTooltipAction as TooltipAction,
  ValidityQuantityChartTooltip,
} from "../../Chart/ChartTooltip/data_symptom/ValidityQuantityChartTooltip";
import { ChartTooltip } from "../../Chart/ChartTooltip";
import { AnalysisDateChartTooltip } from "../../Chart/ChartTooltip/common/AnalysisDateChartTooltip";
import { FlagChartTooltip } from "../../Chart/ChartTooltip/common/FlagChartTooltip";
import {
  DIRECT_INVALID_STATUSES,
  INVALID_STATUSES,
  VALID_STATUSES,
} from "diagnostics-domain/quantity/metrology";
import { CHART_SELECTION_PLOT_LINE_MARGIN } from "../../Chart/hooks/useAnalysisSelectionPlotLines";

const useOptions = ({
  chartRef,
  selectedQuantities,
  series,
  analyses,
  project,
  source,
  timeZone,
  axisGroupedByUnit = false,
  multiColor = false,
  canUpdateQuantityValues = true,
  canDeleteAnalyses = true,
  canPublishAnalyses = true,
}: {
  chartRef: React.RefObject<Highcharts.Chart>;
  selectedQuantities: SelectedQuantity[];
  series: (
    | Highcharts.SeriesLineOptions
    | Highcharts.SeriesArearangeOptions
    | Highcharts.SeriesScatterOptions
  )[];
  analyses: ActiveAnalysis[];
  project: Project;
  source: Source;
  timeZone: string;
  axisGroupedByUnit?: boolean;
  multiColor?: boolean;
  canUpdateQuantityValues?: boolean;
  canDeleteAnalyses?: boolean;
  canPublishAnalyses?: boolean;
}): Options => {
  const quantitiesByName = useQuantityByName(project.id);
  const canUpdateQuantityValuesRef = React.useRef(canUpdateQuantityValues);
  canUpdateQuantityValuesRef.current = canUpdateQuantityValues;
  const canDeleteAnalysesRef = React.useRef(canDeleteAnalyses);
  canDeleteAnalysesRef.current = canDeleteAnalyses;
  const canPublishAnalysesRef = React.useRef(canPublishAnalyses);
  canPublishAnalysesRef.current = canPublishAnalyses;
  const yAxisIds = React.useMemo(
    () => getAxisIds(selectedQuantities, axisGroupedByUnit),
    [selectedQuantities, axisGroupedByUnit],
  );
  const initialYAxisOptions = useYMinMax({
    chartRef,
    yAxisId: yAxisIds,
    scope: "amplitude",
  });

  const xBreaks = useGroupBreaks(project.id, analyses);
  const initialXAxisOptions = useAnalysisXAxis({
    chartRef,
    project,
    source,
    analyses,
    boundaries: "active-analyses",
  });

  useNoDataPlotBands({
    chartRef,
    intervals: useQuantityNoDataIntervals(analyses, selectedQuantities, "amplitude", project.id),
  });
  const { addToast } = useToasts();
  const updateMetrologyOverrides = useUpdateMetrologyOverrides(project.id);

  useTooltipClick(
    chartRef,
    React.useCallback(
      (action: TooltipAction, points: Highcharts.Point[]) => {
        if (action === TooltipAction.Validate || action === TooltipAction.Invalidate) {
          void updateMetrologyOverrides({
            addToast,
            points,
            isValid: action === TooltipAction.Validate,
          });
        } else {
          throw new Error(action satisfies never);
        }
      },
      [addToast, updateMetrologyOverrides],
    ),
  );

  const yAxes = useQuantityYAxes({
    selectedQuantities,
    axisGroupedByUnit,
    multiColor,
  });

  const constantTooltipFormatter = React.useCallback(
    function (this: Highcharts.TooltipFormatterContextObject) {
      const selectedPoints = getMainAxisSelectedPoints(this.series.chart);

      const selectedData = validateInterface(
        io.array(PointMetadata),
        selectedPoints.map(p => p.options.custom),
      );

      const hasSelectedValid = selectedData.some(
        p => "validity" in p && VALID_STATUSES.has(p.validity),
      );

      const hasSelectedInvalid = selectedData.some(
        p => "validity" in p && DIRECT_INVALID_STATUSES.has(p.validity),
      );

      const metadata = validateInterface(PointMetadata, this.point.options.custom);
      const quantityFlags =
        metadata.type === QUANTITY_FLAG_METADATA
          ? metadata.flags.map(m => ({
              name: `${m.name} [${capitalize(
                m.origin === "expected"
                  ? t("common.symptom.expected")
                  : t("common.symptom.measured"),
              )}]`,
              description: m.description,
            }))
          : undefined;

      const human =
        metadata.type === MEASURE_POINT_METADATA || metadata.type === GAP_POINT_METADATA
          ? metadata.human
          : null;
      const validity = metadata.type === MEASURE_POINT_METADATA ? metadata.validity : null;
      const isInvalid = validity ? INVALID_STATUSES.has(validity) : null;
      const isPartial = validity === "VALID_PARTIAL_INPUTS";

      // ChartTooltip
      const selectedCount = this.point.selected ? selectedPoints.length : 0;

      const quantity = quantitiesByName[this.series.name];
      const hasQuantityField = quantity !== undefined;

      const quantityFieldLabel =
        metadata.type === MEASURE_POINT_METADATA
          ? t("project.symptom.chart_tooltip.measured")
          : GAP_POINT_METADATA
            ? t("project.symptom.chart_tooltip.expected")
            : null;
      const quantityFieldValue =
        human === null
          ? "-"
          : formatNumber(human.value as number, {
              sign: human.sign ?? undefined,
              precision: human.precision ?? undefined,
              unit: human.unit ?? undefined,
            });
      const option = quantityFieldLabel
        ? {
            label: quantityFieldLabel,
            value: quantityFieldValue,
          }
        : null;

      const actionDescription = this.point.description;
      const actions: Action<TooltipAction>[] = [
        {
          text: t("measures.chart.tooltip.button.validate"),
          action: TooltipAction.Validate,
          intent: "confirm",
          show: canUpdateQuantityValuesRef.current && hasSelectedInvalid,
        },
        {
          text: t("measures.chart.tooltip.button.invalidate"),
          action: TooltipAction.Invalidate,
          intent: "danger",
          show: canUpdateQuantityValuesRef.current && hasSelectedValid,
        },
      ];
      // const isMeasurePoint = metadata.type === MEASURE_POINT_METADATA;

      return renderToString(
        <ChartTooltip>
          {selectedCount > 1 ? (
            <Text type="h4">
              {t("measures.chart.tooltip.selectedPoints", {
                count: selectedPoints.length,
              })}
            </Text>
          ) : (
            <>
              <AnalysisDateChartTooltip
                date={this.point.x}
                group={metadata.group}
                label={metadata.label}
                timezone={timeZone}
              />
              {hasQuantityField ? (
                <QuantityChartTooltip
                  name={quantity.name}
                  description={quantity.description}
                  option={option}
                />
              ) : null}
            </>
          )}
          {quantityFlags ? <FlagChartTooltip flags={quantityFlags} /> : null}
          {actionDescription ? (
            <ValidityQuantityChartTooltip
              actionDescription={actionDescription}
              actions={actions}
              isInvalid={isInvalid}
              isPartial={isPartial}
              selectedCount={selectedCount}
            />
          ) : null}
        </ChartTooltip>,
      );
    },
    [quantitiesByName, timeZone],
  );

  return React.useMemo(() => {
    const _: Options = {
      initial: {
        xAxis: initialXAxisOptions,
        yAxis: initialYAxisOptions,
      },
      chart: {
        zoomType: yAxes.length > 1 ? "x" : "xy",
        marginTop: CHART_SELECTION_PLOT_LINE_MARGIN,
        panning: { enabled: true, type: selectedQuantities.length > 1 ? "x" : "xy" },
      },
      xAxis: {
        breaks: xBreaks,
      },
      yAxis: [...yAxes, FLAG_AXIS],
      tooltip: {
        sticky: true,
        formatter: constantTooltipFormatter,
      },
      navigator: {
        enabled: true,
        xAxis: {
          breaks: xBreaks,
          max: Date.now(),
        },
      },
      series,
    };
    return _;
  }, [
    initialXAxisOptions,
    // initialYAxisMinMaxFromValidities,
    initialYAxisOptions,
    yAxes,
    selectedQuantities.length,
    xBreaks,
    constantTooltipFormatter,
    series,
  ]);
};

export default useOptions;
